import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { NavigationBar } from '../components/NavigationBar';
import { RootState } from '../store/store';
import { statsService } from '../services/statsService';
import { 
  AcademicCapIcon, 
  BookOpenIcon, 
  CalendarIcon, 
  ClockIcon, 
  UserIcon,
  BoltIcon,
  BeakerIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

interface UserStats {
  loginStreak: number;
  totalQuizzesTaken: number;
  totalQuizScore: number;
  averageQuizScore: number;
  bestQuizScore: number;
  worstQuizScore: number;
  collectionsCreated: number;
  flashcardsCreated: number;
  totalStudyTime: number;
  lastStudySession: Date;
  studySessionsCount: number;
  quizzesByDifficulty: {
    easy: number;
    medium: number;
    hard: number;
  };
  lastLoginDate: string;
  lastActivityDate: string;
  quizHistory?: { score: number; date: string; }[];
}

export const Profile = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await statsService.getUserStats();
        setStats(data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchStats();
    }
  }, [user]);

  // Format study time from minutes to a readable format
  const formatStudyTime = (minutes: number): string => {
    if (!minutes) return '0h';
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return hours > 0 
      ? `${hours}h ${remainingMinutes > 0 ? `${remainingMinutes}m` : ''}`
      : `${remainingMinutes}m`;
  };

  // Calculate average quiz score
  const averageScore = stats && stats.totalQuizzesTaken > 0
    ? Math.min(100, Math.round((stats.totalQuizScore / stats.totalQuizzesTaken)))
    : 0;

  // User membership date - use lastLoginDate or a fallback
  const memberSinceDate = stats?.lastLoginDate 
    ? new Date(stats.lastLoginDate).toLocaleDateString() 
    : new Date().toLocaleDateString();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      <NavigationBar />
      <div className="pt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Profile Header Card */}
            <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-lg rounded-2xl shadow-xl overflow-hidden mb-8">
              {/* Decorative background */}
              <div className="h-32 bg-gradient-to-r from-indigo-500 to-purple-600 dark:from-indigo-600 dark:to-purple-800"></div>
              
              <div className="relative px-6 sm:px-8 pb-8">
                {/* Avatar */}
                <div className="absolute -top-16 left-1/2 transform -translate-x-1/2 sm:-translate-x-0 sm:left-8">
                  <div className="w-32 h-32 rounded-full bg-white dark:bg-gray-700 p-1 shadow-xl">
                    <div className="w-full h-full rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900 dark:to-purple-900 flex items-center justify-center text-4xl font-bold text-indigo-600 dark:text-indigo-400">
                      {user?.username?.charAt(0)?.toUpperCase()}
                    </div>
                  </div>
                </div>
                
                <div className="pt-20 sm:pt-4 sm:pl-36">
                  {/* User info */}
                  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-6">
                    <div>
                      <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{user?.username}</h1>
                      <div className="flex items-center mt-1">
                        <UserIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-1" />
                        <p className="text-gray-600 dark:text-gray-300">{user?.email}</p>
                      </div>
                      {user?.isPremium && (
                        <span className="mt-2 inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-gradient-to-r from-yellow-400 to-yellow-600 text-white">
                          Premium Member
                        </span>
                      )}
                    </div>
                    <div className="mt-4 sm:mt-0">
                      <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-1" />
                        Member since {memberSinceDate}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Profile Content */}
            <div className="grid gap-6 grid-cols-1 md:grid-cols-3">
              {/* Profile Information */}
              <div className="md:col-span-1">
                <div className="bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm rounded-xl p-6 shadow-sm border border-gray-100/50 dark:border-gray-600/50 h-full">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <UserIcon className="h-5 w-5 mr-2 text-indigo-500 dark:text-indigo-400" />
                    Profile Information
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Username</label>
                      <p className="mt-1 text-gray-900 dark:text-white font-medium">{user?.username}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Email</label>
                      <p className="mt-1 text-gray-900 dark:text-white font-medium">{user?.email}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Account Type</label>
                      <p className="mt-1 text-gray-900 dark:text-white font-medium">
                        {user?.isPremium ? 'Premium' : 'Free'}
                      </p>
                    </div>
                    {user?.isPremium && user?.premiumExpiresAt && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Premium Expires</label>
                        <p className="mt-1 text-gray-900 dark:text-white font-medium">
                          {new Date(user.premiumExpiresAt).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Login Streak</label>
                      <div className="flex items-center mt-1">
                        <BoltIcon className="h-4 w-4 text-yellow-500 mr-1" />
                        <p className="text-gray-900 dark:text-white font-medium">
                          {stats?.loginStreak || 0} days
                        </p>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Last Activity</label>
                      <p className="mt-1 text-gray-900 dark:text-white font-medium">
                        {stats?.lastActivityDate ? new Date(stats.lastActivityDate).toLocaleDateString() : 'Today'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Account Statistics */}
              <div className="md:col-span-2">
                <div className="bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm rounded-xl p-6 shadow-sm border border-gray-100/50 dark:border-gray-600/50 h-full">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <AcademicCapIcon className="h-5 w-5 mr-2 text-indigo-500 dark:text-indigo-400" />
                    Learning Statistics
                  </h2>
                  
                  {isLoading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                        <div className="bg-gradient-to-br from-white to-indigo-50 dark:from-gray-800 dark:to-indigo-900/20 rounded-lg p-4 shadow-sm">
                          <div className="flex items-center mb-2">
                            <BookOpenIcon className="h-5 w-5 text-indigo-500 mr-2" />
                            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Collections</h3>
                          </div>
                          <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{stats?.collectionsCreated || 0}</p>
                          {!user?.isPremium && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Limit: 5</p>
                          )}
                        </div>
                        
                        <div className="bg-gradient-to-br from-white to-purple-50 dark:from-gray-800 dark:to-purple-900/20 rounded-lg p-4 shadow-sm">
                          <div className="flex items-center mb-2">
                            <AcademicCapIcon className="h-5 w-5 text-purple-500 mr-2" />
                            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Avg. Score</h3>
                          </div>
                          <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{averageScore}%</p>
                        </div>
                        
                        <div className="bg-gradient-to-br from-white to-green-50 dark:from-gray-800 dark:to-green-900/20 rounded-lg p-4 shadow-sm">
                          <div className="flex items-center mb-2">
                            <ClockIcon className="h-5 w-5 text-green-500 mr-2" />
                            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Study Time</h3>
                          </div>
                          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                            {formatStudyTime(stats?.totalStudyTime || 0)}
                          </p>
                        </div>
                        
                        <div className="bg-gradient-to-br from-white to-amber-50 dark:from-gray-800 dark:to-amber-900/20 rounded-lg p-4 shadow-sm">
                          <div className="flex items-center mb-2">
                            <BookOpenIcon className="h-5 w-5 text-amber-500 mr-2" />
                            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Flashcards</h3>
                          </div>
                          <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{stats?.flashcardsCreated || 0}</p>
                          {!user?.isPremium && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Limit: 10 per collection</p>
                          )}
                        </div>
                        
                        <div className="bg-gradient-to-br from-white to-rose-50 dark:from-gray-800 dark:to-rose-900/20 rounded-lg p-4 shadow-sm">
                          <div className="flex items-center mb-2">
                            <AcademicCapIcon className="h-5 w-5 text-rose-500 mr-2" />
                            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Quizzes Taken</h3>
                          </div>
                          <p className="text-2xl font-bold text-rose-600 dark:text-rose-400">{stats?.totalQuizzesTaken || 0}</p>
                        </div>
                        
                        {user?.isPremium && (
                          <>
                            <div className="bg-gradient-to-br from-white to-yellow-50 dark:from-gray-800 dark:to-yellow-900/20 rounded-lg p-4 shadow-sm">
                              <div className="flex items-center mb-2">
                                <ChartBarIcon className="h-5 w-5 text-yellow-500 mr-2" />
                                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Best Score</h3>
                              </div>
                              <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats?.bestQuizScore || 0}%</p>
                            </div>
                            
                            {/* Premium-only stats */}
                            <div className="bg-gradient-to-br from-white to-blue-50 dark:from-gray-800 dark:to-blue-900/20 rounded-lg p-4 shadow-sm md:col-span-3">
                              <div className="flex items-center mb-2">
                                <BeakerIcon className="h-5 w-5 text-blue-500 mr-2" />
                                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Quiz Difficulty Breakdown</h3>
                              </div>
                              <div className="grid grid-cols-3 gap-2 mt-2">
                                <div>
                                  <p className="text-sm text-gray-500 dark:text-gray-400">Easy</p>
                                  <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                                    {stats?.quizzesByDifficulty?.easy || 0}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-500 dark:text-gray-400">Medium</p>
                                  <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                                    {stats?.quizzesByDifficulty?.medium || 0}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-500 dark:text-gray-400">Hard</p>
                                  <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                                    {stats?.quizzesByDifficulty?.hard || 0}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>

                      {!user?.isPremium && (
                        <div className="mt-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg border border-indigo-100 dark:border-indigo-700/30">
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            Upgrade to Premium for detailed statistics, unlimited collections, and advanced performance tracking
                          </p>
                          <button 
                            onClick={() => window.location.href = '/pricing'} 
                            className="mt-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-sm font-medium rounded-md hover:from-indigo-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            Upgrade Now
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};